import React, { useCallback, useState } from "react"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { useQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { DEFAULT_PAGE_SIZE } from "@hydrologiq/shared/config"

export interface ActivityFilters {
  status?: string
  kind?: string
  customer?: string
  site?: string
  asset?: string
  name?: string
  order?: string
  batch?: string
  fulfillment?: string
  source_site?: string
  destination_site?: string
}

export interface ActivitySort {
  field?: string
  direction?: "asc" | "desc"
}

// Default values
const DEFAULT_FILTERS: ActivityFilters = {}
export const DEFAULT_SORT: ActivitySort = {
  field: "start",
  direction: "asc",
}

interface UseActivitiesReturn {
  activities: gqlTypes.Activity[]
  pagination: gqlTypes.PaginationMeta | undefined
  loading: boolean
  refreshing: boolean
  error: string | null
  filters: ActivityFilters
  sort: ActivitySort | null
  filterOptions: gqlTypes.ActivityFilterOptions | undefined
  defaultFilters: ActivityFilters
  defaultSort: ActivitySort
  fetchActivities: (
    page?: number,
    pageSize?: number,
    newFilters?: ActivityFilters,
    newSort?: ActivitySort | null
  ) => Promise<void>
  setFilters: (filters: ActivityFilters) => void
  setSort: (sort: ActivitySort | null) => void
  clearFilters: () => void
  refetch: () => Promise<void>
}

export function useActivities(
  initialPageSize: number = DEFAULT_PAGE_SIZE,
  skipInitialFetch: boolean = false,
  initialFilters?: ActivityFilters,
  defaultSortAsc: boolean = true
): UseActivitiesReturn {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [filters, setFiltersState] = useState<ActivityFilters>(initialFilters || DEFAULT_FILTERS)
  const defaultSort: ActivitySort = {
    field: DEFAULT_SORT.field,
    direction: defaultSortAsc ? "asc" : "desc",
  }
  const [sort, setSortState] = useState<ActivitySort | null>(defaultSort)
  const [shouldFetch, setShouldFetch] = useState(!skipInitialFetch)
  const [activities, setActivities] = useState<gqlTypes.Activity[]>([])
  const [pagination, setPagination] = useState<gqlTypes.PaginationMeta | undefined>(undefined)
  const [filterOptions, setFilterOptions] = useState<gqlTypes.ActivityFilterOptions | undefined>(undefined)

  // Build query variables
  const buildVariables = useCallback(
    (page: number, perPage: number, queryFilters: ActivityFilters, querySort: ActivitySort | null) => {
      const variables: any = {
        page,
        per_page: perPage,
      }

      // Add filter variables (only include non-empty values)
      if (queryFilters.status) variables.status = queryFilters.status
      if (queryFilters.kind) variables.kind = queryFilters.kind
      if (queryFilters.customer) variables.customer = queryFilters.customer
      if (queryFilters.site) variables.site = queryFilters.site
      if (queryFilters.asset) variables.asset = queryFilters.asset
      if (queryFilters.name) variables.name = queryFilters.name
      if (queryFilters.order) variables.order = queryFilters.order
      if (queryFilters.batch) variables.batch = queryFilters.batch
      if (queryFilters.fulfillment) variables.fulfillment = queryFilters.fulfillment
      if (queryFilters.source_site) variables.source_site = queryFilters.source_site
      if (queryFilters.destination_site) variables.destination_site = queryFilters.destination_site
      // Add sort variable
      if (querySort?.field && querySort?.direction) {
        variables.sort = `${querySort.field}:${querySort.direction}`
      }

      return variables
    },
    []
  )

  const {
    data,
    error: queryError,
    refetch: apolloRefetch,
    loading: apolloLoading,
    networkStatus,
  } = useQueryWithEndpoint(
    gqlTypes.GetActivitiesDocument,
    {
      variables: buildVariables(currentPage, pageSize, filters, sort),
      fetchPolicy: "cache-first",
      nextFetchPolicy: "cache-first",
      errorPolicy: "all",
      notifyOnNetworkStatusChange: true,
      skip: !shouldFetch,
    },
    "primary"
  )

  // Update filters when initialFilters changes
  const initialFiltersRef = React.useRef(initialFilters)
  React.useEffect(() => {
    if (
      initialFilters &&
      JSON.stringify(initialFilters) !== JSON.stringify(initialFiltersRef.current)
    ) {
      initialFiltersRef.current = initialFilters
      setFiltersState(initialFilters)
      setShouldFetch(true)
    }
  }, [initialFilters])

  // Update local state when Apollo data changes
  React.useEffect(() => {
    if (queryError) {
      return
    }

    if (data?.activities) {
      setActivities(data.activities.data as gqlTypes.Activity[])
      setPagination(data.activities.meta)

      // Update filter options if available
      if (data.activities.filter_options) {
        setFilterOptions(data.activities.filter_options as any)
      }
    }
  }, [data, queryError])

  const fetchActivities = useCallback(
    async (
      page: number = currentPage,
      perPage: number = pageSize,
      newFilters: ActivityFilters = filters,
      newSort: ActivitySort | null = sort
    ) => {
      try {
        // Update local state
        setCurrentPage(page)
        setPageSize(perPage)
        setFiltersState(newFilters)
        setSortState(newSort)

        // Enable fetching if it was skipped initially
        if (!shouldFetch) {
          setShouldFetch(true)
        }

        // Use Apollo refetch with new variables
        await apolloRefetch(buildVariables(page, perPage, newFilters, newSort))
      } catch (err) {
        console.error("Failed to load activities", err)
      }
    },
    [apolloRefetch, buildVariables, currentPage, pageSize, filters, sort, shouldFetch]
  )

  const setFilters = useCallback(
    (newFilters: ActivityFilters) => {
      setFiltersState(newFilters)
      fetchActivities(1, pageSize, newFilters, sort)
    },
    [fetchActivities, pageSize, sort]
  )

  const setSort = useCallback(
    (newSort: ActivitySort | null) => {
      setSortState(newSort)
      fetchActivities(1, pageSize, filters, newSort)
    },
    [fetchActivities, pageSize, filters]
  )

  const clearFilters = useCallback(() => {
    setFiltersState(DEFAULT_FILTERS)
    setSortState(defaultSort)
    fetchActivities(1, pageSize, DEFAULT_FILTERS, defaultSort)
  }, [fetchActivities, pageSize, defaultSort])

  const refetch = useCallback(async () => {
    try {
      await apolloRefetch()
    } catch (err) {
      console.error("Failed to refresh activities", err)
    }
  }, [apolloRefetch])

  // Compute refreshing state
  const refreshing = networkStatus === 4
  const loading = apolloLoading && (networkStatus === 1 || networkStatus === 2)
  const error = queryError ? queryError.message : null

  return {
    activities,
    pagination,
    loading,
    refreshing,
    error,
    filters,
    sort,
    filterOptions,
    defaultFilters: DEFAULT_FILTERS,
    defaultSort: defaultSort,
    fetchActivities,
    setFilters,
    setSort,
    clearFilters,
    refetch,
  }
}
